import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { Route as ReactDOMRoute, Redirect, useHistory } from "react-router-dom";
import MainContainerWithSidebar from "../../components/MainContainerWithSidebar";
import LoadingOverlay from "../../components/LoadingOverlay";
import MainStaffOperationsHeader from "../../components/MainStaffOperationsHeader";

const Route = ({
  isPrivate = false,
  component: Component,
  roles,
  code,
  ...rest
}) => {
  const { user, serviceProviderLoggingAsAnExhibitor, adminToken, loading } =
    useSelector((state) => state.auth);
  const history = useHistory();

  const isServiceProvider = useCallback(
    () =>
      !adminToken &&
      (serviceProviderLoggingAsAnExhibitor ||
        user?.role?.type === "SERVICE_PROVIDER"),
    [adminToken, serviceProviderLoggingAsAnExhibitor, user?.role?.type]
  );
  const isExhibitor = useCallback(
    () => !adminToken && user?.role?.type === "EXHIBITOR",
    [adminToken, user?.role?.type]
  );
  const hasAccessedStaffJourney =
    sessionStorage.getItem("StaffJourneyAccessed") === "true";

  const isStaff = useCallback(
    () => !adminToken && user?.role?.type === "STAFF",
    [adminToken, user?.role?.type]
  );

  const havePermission = useCallback(() => {
    if (!code) {
      return true;
    }

    const permissions =
      user?.permissionCategories?.reduce((array, item) => {
        let arraySanitized = [];
        item.permissions.forEach((value) => {
          arraySanitized.push(value.code);
        });
        return [...array.concat(arraySanitized)];
      }, []) || [];

    return permissions.includes(code);
  }, [code, user]);

  if (history.location.pathname === "/logout" && user) {
    return (
      <ReactDOMRoute
        {...rest}
        render={() => {
          return <Component />;
        }}
      />
    );
  }

  if (loading) {
    return <div></div>;
  }

  if (user && !havePermission()) {
    const fallbackPath =
      isServiceProvider() || isExhibitor() ? "/home" : "/dashboard";

    // Mitigacao de bloqueio: nao redirecionar para a propria rota atual. Como o alvo de fallback
    // pode exigir um `code` que o usuario tambem nao possui (ex.: /dashboard exige
    // ACCESS_KNOWLEDGE_BASE), redirecionar sempre para /dashboard causaria loop infinito e travaria
    // o usuario na landing pos-login. Quando ja se esta na rota de fallback, deixa renderizar em vez
    // de repetir o redirect.
    if (history.location.pathname !== fallbackPath) {
      return (
        <Redirect
          to={{
            pathname: fallbackPath,
          }}
        />
      );
    }
  }

  if (user && isPrivate && roles) {
    if (roles.includes(user.role.type)) {
      return (
        <ReactDOMRoute
          {...rest}
          render={() => {
            return hasAccessedStaffJourney ? (
              <MainStaffOperationsHeader>
                <Component />
              </MainStaffOperationsHeader>
            ) : (
              <MainContainerWithSidebar>
                <Component />
              </MainContainerWithSidebar>
            );
          }}
        />
      );
    }
    return (
      <Redirect
        to={{
          pathname: "/dashboard",
        }}
      />
    );
  }

  return (
    <ReactDOMRoute
      {...rest}
      render={({ location }) => {
        /* isPrivate/user (is signed)
          true/true --> OK
          true/false --> Redirect to Logon
          false/true --> Redirect to Dashboard
          false/false --> OK
        */

        return isPrivate === !!user ? (
          isPrivate ? (
            <MainContainerWithSidebar>
              <Component />
            </MainContainerWithSidebar>
          ) : (
            <LoadingOverlay>
              <Component />
            </LoadingOverlay>
          )
        ) : (
          <Redirect
            to={{
              pathname: isPrivate
                ? "/"
                : isServiceProvider() || isExhibitor()
                ? "/home"
                : "/dashboard",
              state: { from: location },
            }}
          />
        );
      }}
    />
  );
};

export default Route;
